import React, {useEffect} from 'react';
import ReactDOM from 'react-dom';
import {Route, Router, Switch} from 'react-router-dom'
import ProtectedRoute from './common/ProtectedRoute';
import ActivateAccount from './views/ActivateAccount';
import MobileDetect from 'mobile-detect';
import eventEmitter, {notifyBrowserUnload} from './common/Events/EventSource';
import history from './history';
// Views
import RegisterFBO from './views/Register/FBO/RegisterFBO';
import RegisterOwner from './views/Register/TractorOwner/RegisterTractorOwner';

import Login from './views/Login/';
import ForgotPassword from './views/ForgotPassword';
import Proxy from './common/TTLAsyncProxy';
import IndexRoute from "./common/IndexRoute";
import RouterPrivate from './common/RouterPrivate';
import SessionStateManager from './common/SessionStateManager';
import 'react-toastify/dist/ReactToastify.min.css';
import Page404 from './views/Page404';
// Containers
import Full from './containers/Full/';
import NotificationHandler from "./common/NotificationHandler";
import AppGlobals from "./common/AppGlobals";
import NavigationHelper from "./common/NavigationHelper";
import {LocalSettingsManager} from "./common/LocalSettings/LocalSettings";
import {UsersEndpoints} from "./common/ProxyEndpoints";
import 'react-image-lightbox/style.css';
import RegisterFarmer from "./views/Register/Farmer/RegisterFarmer";
import {initLocationsData} from "./data/locations";
import {DefaultToastContainer, ToastProvider, useToasts} from 'react-toast-notifications';
import {initialize as InitializeToastManager} from './react-notifications';
import {subscribeNotificationChannels} from "./common/UserNotifications/Subscriptions";


const ToastContainer = props => (
    <DefaultToastContainer
        className="toast-container"
        style={{ zIndex: 99999999 }}
        {...props}
    />
);

//  ---------------------------------------------------
//              Initialize application here
//  ---------------------------------------------------


/**
 * Initialize web api proxy
 */
const initCoreProxy = () => {

    const serverAddress = (process.env.REACT_APP_CORE_API_SECURE === 'true' ? 'https' : 'http' ) + '://' + process.env.REACT_APP_CORE_API_HOST;

    //  Remove this line before production
    const env = process.env.NODE_ENV;
    if(env !== 'production') {
        console.dir(process.env);
        console.log('Server Address: ' + serverAddress);
        console.log(`Application deployed in ${env} mode`);
    }

    //  create proxy
    const proxy = new Proxy(process.env.REACT_APP_CORE_API_KEY,serverAddress);

    //  set history object
    proxy.setHistory(history);

    //
    AppGlobals.set('proxy', proxy);
};

const initEvents = () => {
    AppGlobals.set('events', eventEmitter );
};

/**
 * Initialize platform detector
 */
const initPlatformDetector = () => {

    const md = new MobileDetect(window.navigator.userAgent);
    AppGlobals.set('mobile_detector',md);
    AppGlobals.set('isMobile',md.mobile());
    AppGlobals.set('isTablet',md.tablet());
};

/**
 * Initializes a global session manager for keeping application state irrespective of views loaded
 * @param key
 */
const initSessionManager = (key) => {

    const session = new SessionStateManager( key , true );
    AppGlobals.set('sessionManager',session);

};

const initFullscreenDetection = () => {

    //
    window.document.addEventListener("fullscreenchange", onFullScreenChange, false);
    window.document.addEventListener("webkitfullscreenchange", onFullScreenChange, false);
    window.document.addEventListener("mozfullscreenchange", onFullScreenChange, false);

    function onFullScreenChange() {
        //
        var fullscreenElement = document.fullscreenElement || document.mozFullScreenElement || document.webkitFullscreenElement;

        // if in fullscreen mode fullscreenElement won't be null
        AppGlobals.set('isFullscreen', !!fullscreenElement);
    }

};

const handleUserAuthenticated = async () => {

    const user = Proxy.getInstance().getUserInfo();

    //  set global settings
    AppGlobals.set('settings', LocalSettingsManager.get(user.id));

    //  initialize user notifications
    await subscribeNotificationChannels(user);
};

const initHandlers = () => {

    const events = AppGlobals.get('events');
    const proxy = AppGlobals.get('proxy');

    //  handle browser unload
    window.addEventListener('beforeunload',() => {

        //  get proxy
        const proxy = AppGlobals.get('proxy');

        //  check whether user is authenticated before
        if(proxy.isAuthenticated()) {

            //  Get user info
            const user = proxy.getUserInfo();
            const settings = AppGlobals.get('settings');

            //  save settings
            if(settings){
                LocalSettingsManager.save(user.id, settings );
            }

            //  Notify browser unload
            notifyBrowserUnload();
        }

    });

    //  register channels based on user roles
    events.on('user_login',(user) => handleUserAuthenticated());

    events.on('user_logout', () => {
        NotificationHandler.getInstance().disconnect();
    });


    if(proxy.isAuthenticated()){

        //
        const user = proxy.getUserInfo();

        //  set global settings
        AppGlobals.set('settings', LocalSettingsManager.get(user.id));

    }

};

/**
 * Startup application
 */
(function startupInit() {

    //  initialize global store
    AppGlobals.init();

    //  init navigation helper
    NavigationHelper.init(history);

    //  initialize core-api proxy
    initCoreProxy();

    //  initialize platform detector
    initPlatformDetector();

    //  initialize session manager
    initSessionManager('ttl_ng_core_settings');

    //  initialize event manager
    initEvents();

    //  fullscreen
    initFullscreenDetection();

    //  Last thing to do
    initHandlers();

    //
    const proxy = Proxy.getInstance();
    if(proxy.isAuthenticated()){
        handleUserAuthenticated();
    }

    //  load location data
    initLocationsData(proxy);

}());

/**
 * Application Component
 * @return {*}
 * @constructor
 */
const App = () => {

    const toastManager = useToasts();

    useEffect(() => {

        const proxy = Proxy.getInstance();
        if(proxy.isAuthenticated()) {

            //  refresh the current user
            proxy.processRequest( UsersEndpoints.getMyself() ).then(response => {
                return proxy.setUserInfo( response.getPayload() );
            });

        }


        //  setup toast manager
        InitializeToastManager(toastManager);

    },[]);

    return <Router history={history}>
        <Switch>
            <ProtectedRoute path="/login" name="Login" component={Login} allowAnonymous={false} />
            <ProtectedRoute path="/register/fbo" name="Register FBO" component={RegisterFBO}  allowAnonymous={false}  />
            <ProtectedRoute path="/register/owner" name="Register Owner" component={RegisterOwner}  allowAnonymous={false}  />
            <ProtectedRoute path="/register/farmer" name="Register Farmer" component={RegisterFarmer} allowAnonymous={false}/>
            <ProtectedRoute path="/forgotpassword" name="Forgot Password" component={ForgotPassword} allowAnonymous={false} />
            <ProtectedRoute path="/activate" name="Activate Account" component={ActivateAccount} />
            <Route path="/page-not-found" component={Page404}/>
            <RouterPrivate path="/" name="Home" component={Full}/>
            <IndexRoute/>
        </Switch>
    </Router>;

};



ReactDOM.render(<ToastProvider components={{ ToastContainer }}><App/></ToastProvider>, document.getElementById('root'));



// WEBPACK FOOTER //
// ./src/index.js